import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Field, FieldProps } from 'formik';
import { Form, AutoComplete } from 'antd';
import { get as _get } from 'lodash';
import { usePlacesAutocomplete } from './usePlacesAutocomplete';

interface FormInputPlacesProps {
  name: string;
  handleAddressChange: (addressComponents: any) => void;
}

const FormInputPlaces: React.FC<FormInputPlacesProps> = ({ handleAddressChange, name }) => {
  const { t } = useTranslation();

  const { setQuery, suggestions, select } = usePlacesAutocomplete({
    debounceMs: 150,
    language: 'en',
  });

  const handleSelectSuggestion = useCallback(
    async (suggestionId: string, formSetFieldValue: (field: string, value: any) => void) => {
      const suggestion = suggestions.find(s => s.id === suggestionId);
      if (!suggestion) return;

      try {
        const place = await select(suggestion);
        if (!place) return;
        const addressDetails: any = place.toJSON();
        if (addressDetails?.addressComponents) {
          const addressText = addressDetails.displayName;
          formSetFieldValue(name, addressText);
          handleAddressChange(addressDetails.addressComponents);
        }
      } catch (error) {
        /* empty */
      }
    },
    [suggestions, select, handleAddressChange, name]
  );

  const options: { value: string; label: React.ReactNode }[] = suggestions.map(suggestion => ({
    value: suggestion.id,
    label: (
      <>
        <div className="text-sm">{suggestion.mainText}</div>
        {suggestion.secondaryText && (
          <div className="text-xs text-gray-500">{suggestion.secondaryText}</div>
        )}
      </>
    ),
  }));

  const fieldContent = ({ field, form }: FieldProps) => {
    const { errors, touched } = form;
    const showValidationInfo = !!(_get(errors, name) && _get(touched, name));

    const handleSearch = (value: string) => {
      form.setFieldValue(name, value);
      setQuery(value);
    };

    const handleSelect = (value: string) => {
      handleSelectSuggestion(value, form.setFieldValue);
    };

    return (
      <Form.Item
        label={t('companyProfile:address')}
        hasFeedback
        validateStatus={showValidationInfo ? 'error' : ''}
        help={(showValidationInfo && _get(errors, name))?.toString() || undefined}
        required
      >
        <AutoComplete
          value={field.value}
          onSearch={handleSearch}
          onSelect={handleSelect}
          options={options}
          placeholder={t('companyProfile:enterAddress')}
          getPopupContainer={n => n?.parentElement || document.body}
        />
      </Form.Item>
    );
  };

  return <Field name={name}>{fieldContent}</Field>;
};

export default FormInputPlaces;
